import { Fragment, type ReactNode } from 'react';
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { z } from 'zod';

import { useOrganizationTimezone } from '@apps/web/context/timezone_provider';
import { cn } from '@apps/web/lib/utils';
import {
    type AdminAgentArtifact,
    ArtifactEvidence,
    ArtifactFrame,
    ArtifactLifecycleStatusBadge,
    ArtifactMetaDisclosure,
} from '@apps/web/routes/_app/admin/agent/artifacts/frame';
import { type AdminArtifactRendererProps } from '@apps/web/routes/_app/admin/agent/artifacts/shared';

const CHART_COLORS = [
    '#2f5aa8',
    '#1f7a3a',
    '#a06a0a',
    '#c0392b',
    '#6b6b6b',
    '#4b6bff',
] as const;
const MAX_RENDERED_POINTS = 5_000;
// Above this per-series point count, always-on markers read as noise, so
// line/area series drop them and rely on activeDot for hover inspection.
const MAX_DOTTED_POINTS = 12;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const xValueSchema = z.union([z.string().min(1), z.number().finite()]);

const chartAxisSchema = z.object({
    label: z.string().min(1),
    type: z.enum(['temporal', 'quantitative', 'categorical']),
});

const chartPointSchema = z.object({
    x: xValueSchema,
    y: z.number().finite().nullable(),
});

const chartSeriesSchema = z.object({
    name: z.string().min(1),
    points: z.array(chartPointSchema),
});

const chartPayloadSchema = z
    .object({
        title: z.string().min(1),
        chart_kind: z.enum(['line', 'bar', 'area']),
        axes: z.object({
            x: chartAxisSchema,
            y: chartAxisSchema,
        }),
        series: z.array(chartSeriesSchema),
    })
    .superRefine((payload, context) => {
        const seenSeriesNames = new Set<string>();
        for (const [seriesIndex, series] of payload.series.entries()) {
            if (seenSeriesNames.has(series.name)) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Series names must be unique.',
                    path: ['series', seriesIndex, 'name'],
                });
            }
            seenSeriesNames.add(series.name);

            for (const [pointIndex, point] of series.points.entries()) {
                if (payload.axes.x.type === 'temporal') {
                    if (
                        typeof point.x !== 'string' ||
                        parseTemporalInstant(point.x) === null
                    ) {
                        context.addIssue({
                            code: z.ZodIssueCode.custom,
                            message:
                                'Temporal x values must be ISO date or datetime strings.',
                            path: [
                                'series',
                                seriesIndex,
                                'points',
                                pointIndex,
                                'x',
                            ],
                        });
                    }
                }
                if (
                    payload.axes.x.type === 'quantitative' &&
                    typeof point.x !== 'number'
                ) {
                    context.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Quantitative x values must be numeric.',
                        path: [
                            'series',
                            seriesIndex,
                            'points',
                            pointIndex,
                            'x',
                        ],
                    });
                }
            }
        }
    });

type ParsedChartPayload = z.infer<typeof chartPayloadSchema>;

type ChartRow = {
    readonly orderKey: string;
    readonly x: number | string;
    readonly xLabel: string;
    readonly values: Record<string, number | null>;
};

type SeriesDescriptor = {
    readonly color: string;
    readonly dataKey: string;
    readonly name: string;
    readonly pointCount: number;
};

export function AdminChartArtifact({ artifact }: AdminArtifactRendererProps) {
    const { timezone: organizationTimezone } = useOrganizationTimezone();
    const parsed = chartPayloadSchema.safeParse(artifact.payload);
    if (!parsed.success) {
        return (
            <ArtifactFrame
                artifact={artifact}
                status={
                    <ChartStatus
                        artifact={artifact}
                        tone="error"
                        label="Malformed chart"
                    />
                }
            >
                <ArtifactEvidence artifact={artifact} />
                <ChartMessage tone="error">
                    This chart artifact does not match the admin.chart@1
                    contract, so the admin UI cannot render it safely.
                </ChartMessage>
            </ArtifactFrame>
        );
    }

    const payload = parsed.data;
    const series = payload.series.map((item, index) => ({
        color: CHART_COLORS[index % CHART_COLORS.length] ?? CHART_COLORS[0],
        dataKey: `series_${index}`,
        name: item.name,
        pointCount: item.points.length,
    }));
    const chartTimezone = resolveChartTimezone({
        artifactTimeWindow: artifact.time_window,
        organizationTimezone,
    });
    const rows = buildChartRows(payload, series, chartTimezone);
    const pointCount = payload.series.reduce(
        (count, item) => count + item.points.length,
        0,
    );
    const chartTooLarge = pointCount > MAX_RENDERED_POINTS;
    return (
        <ArtifactFrame
            artifact={artifact}
            title={payload.title}
            status={
                <ChartStatus
                    artifact={artifact}
                    tone="ready"
                    label={formatChartKind(payload.chart_kind)}
                />
            }
        >
            {pointCount === 0 ? (
                <ChartMessage tone="empty">
                    No data points were supplied for this chart.
                </ChartMessage>
            ) : chartTooLarge ? (
                <ChartMessage tone="warning">
                    This chart is too large to render inline safely. Narrow the
                    query or aggregate or bucket the data before plotting. The
                    inline renderer is capped at{' '}
                    {MAX_RENDERED_POINTS.toLocaleString()} points.
                </ChartMessage>
            ) : (
                <Fragment>
                    {series.length > 0 ? <ChartLegend series={series} /> : null}
                    <div className="rounded-[6px] border border-[var(--aa-border,#e8e6e1)] bg-[var(--aa-surface,#f7f7f5)] px-2 py-2">
                        <ResponsiveContainer width="100%" height={240}>
                            {renderChart({
                                chartKind: payload.chart_kind,
                                chartTimezone,
                                payload,
                                rows,
                                series,
                            })}
                        </ResponsiveContainer>
                    </div>
                </Fragment>
            )}
            <ArtifactMetaDisclosure>
                <ArtifactEvidence artifact={artifact} />
                <ChartMetadata payload={payload} pointCount={pointCount} />
            </ArtifactMetaDisclosure>
        </ArtifactFrame>
    );
}

function ChartMetadata({
    payload,
    pointCount,
}: {
    readonly payload: ParsedChartPayload;
    readonly pointCount: number;
}) {
    return (
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 rounded-[6px] border border-[var(--aa-border,#e8e6e1)] bg-[var(--aa-canvas,#ffffff)] px-3 py-2 sm:grid-cols-4">
            <MetadataCell
                label="Kind"
                value={formatChartKind(payload.chart_kind)}
            />
            <MetadataCell
                label={payload.axes.x.label}
                value={payload.axes.x.type}
            />
            <MetadataCell
                label={payload.axes.y.label}
                value={payload.axes.y.type}
            />
            <MetadataCell label="Points" value={String(pointCount)} />
        </div>
    );
}

function MetadataCell({
    label,
    value,
}: {
    readonly label: string;
    readonly value: string;
}) {
    // min-w-0 + break-words keep long unbreakable tokens (axis labels,
    // "quantitative") wrapping inside their minmax(0,1fr) grid track instead
    // of painting over the neighboring cell in narrow sidebars.
    return (
        <div className="min-w-0">
            <div className="aa-caption break-words">{label}</div>
            <div className="aa-mono aa-tnum text-[12px] break-words">
                {value}
            </div>
        </div>
    );
}

function ChartLegend({
    series,
}: {
    readonly series: ReadonlyArray<SeriesDescriptor>;
}) {
    return (
        <div className="flex flex-wrap gap-1.5">
            {series.map((item) => (
                <span
                    key={item.dataKey}
                    className="aa-chip inline-flex items-center gap-1.5 px-2 py-1 text-[10px]"
                >
                    <span
                        className="inline-block size-1.5 rounded-full"
                        style={{ backgroundColor: item.color }}
                    />
                    {item.name}
                </span>
            ))}
        </div>
    );
}

function ChartMessage({
    children,
    tone,
}: {
    readonly children: ReactNode;
    readonly tone: 'empty' | 'error' | 'warning';
}) {
    const toneClass =
        tone === 'error'
            ? 'border-[var(--aa-danger-border,#e9c3bd)] bg-[var(--aa-danger-soft,#fbe9e6)] text-[var(--aa-danger,#c0392b)]'
            : tone === 'warning'
              ? 'border-[var(--aa-accent-border,#e6d3a1)] bg-[var(--aa-accent-soft,#fbf4e5)] text-[var(--aa-accent-ink,#8a5b0a)]'
              : 'border-[var(--aa-border,#e8e6e1)] bg-[var(--aa-surface,#f7f7f5)] text-[var(--aa-ink-muted,#6b6b6b)]';
    return (
        <div
            className={cn(
                'rounded-[6px] border px-3 py-2 text-xs leading-relaxed',
                toneClass,
            )}
        >
            {children}
        </div>
    );
}

function ChartStatus({
    artifact,
    label,
    tone,
}: {
    readonly artifact: AdminAgentArtifact;
    readonly label: string;
    readonly tone: 'error' | 'ready';
}) {
    if (tone === 'error') {
        return (
            <span className="inline-flex items-center gap-2">
                <ArtifactLifecycleStatusBadge
                    lifecycleStatus={artifact.lifecycle_status}
                />
                <span className="aa-chip aa-chip-danger inline-flex items-center gap-1.5 px-2 py-1 text-[10px]">
                    {label}
                </span>
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-2">
            <ArtifactLifecycleStatusBadge
                lifecycleStatus={artifact.lifecycle_status}
            />
            <span className="aa-chip aa-chip-info px-2 py-1 text-[10px]">
                {label}
            </span>
        </span>
    );
}

function renderChart(input: {
    readonly chartKind: ParsedChartPayload['chart_kind'];
    readonly chartTimezone: string;
    readonly payload: ParsedChartPayload;
    readonly rows: ReadonlyArray<ChartRow>;
    readonly series: ReadonlyArray<SeriesDescriptor>;
}) {
    const commonProps = {
        data: input.rows.map((row) => ({
            x: row.x,
            xLabel: row.xLabel,
            ...row.values,
        })),
        margin: { top: 8, right: 8, bottom: 8, left: -16 },
    };
    switch (input.chartKind) {
        case 'line':
            return (
                <LineChart {...commonProps} data-testid="chart-line">
                    <ChartScaffold
                        payload={input.payload}
                        timezone={input.chartTimezone}
                    />
                    {input.series.map((series) => (
                        <Line
                            key={series.dataKey}
                            dataKey={series.dataKey}
                            name={series.name}
                            stroke={series.color}
                            strokeWidth={2}
                            dot={
                                series.pointCount > MAX_DOTTED_POINTS
                                    ? false
                                    : { r: 2 }
                            }
                            activeDot={{ r: 4 }}
                            type="monotone"
                        />
                    ))}
                </LineChart>
            );
        case 'area':
            return (
                <AreaChart {...commonProps} data-testid="chart-area">
                    <ChartScaffold
                        payload={input.payload}
                        timezone={input.chartTimezone}
                    />
                    {input.series.map((series) => (
                        <Area
                            key={series.dataKey}
                            dataKey={series.dataKey}
                            name={series.name}
                            stroke={series.color}
                            fill={series.color}
                            fillOpacity={0.18}
                            strokeWidth={2}
                            dot={
                                series.pointCount > MAX_DOTTED_POINTS
                                    ? false
                                    : { r: 2 }
                            }
                            activeDot={{ r: 4 }}
                            type="monotone"
                        />
                    ))}
                </AreaChart>
            );
        case 'bar':
            return (
                <BarChart {...commonProps} data-testid="chart-bar">
                    <ChartScaffold
                        payload={input.payload}
                        timezone={input.chartTimezone}
                    />
                    {input.series.map((series) => (
                        <Bar
                            key={series.dataKey}
                            dataKey={series.dataKey}
                            name={series.name}
                            fill={series.color}
                            radius={[4, 4, 0, 0]}
                        />
                    ))}
                </BarChart>
            );
        default:
            return assertNever(input.chartKind);
    }
}

function ChartScaffold({
    payload,
    timezone,
}: {
    readonly payload: ParsedChartPayload;
    readonly timezone: string;
}) {
    const quantitativeXAxis = payload.axes.x.type === 'quantitative';
    return (
        <Fragment>
            <CartesianGrid
                stroke="var(--aa-border,#e8e6e1)"
                strokeDasharray="2 3"
                vertical={false}
            />
            <XAxis
                axisLine={{
                    stroke: 'var(--aa-border-strong,#d6d3ca)',
                }}
                dataKey={quantitativeXAxis ? 'x' : 'xLabel'}
                domain={quantitativeXAxis ? ['auto', 'auto'] : undefined}
                minTickGap={20}
                tick={{
                    fill: 'var(--aa-ink-muted,#6b6b6b)',
                    fontFamily: 'var(--aa-font-mono, monospace)',
                    fontSize: 11,
                }}
                tickFormatter={
                    quantitativeXAxis
                        ? (value) =>
                              formatAxisValue(
                                  value,
                                  payload.axes.x.type,
                                  timezone,
                              )
                        : undefined
                }
                tickLine={false}
                type={quantitativeXAxis ? 'number' : 'category'}
            />
            <YAxis
                axisLine={false}
                tick={{
                    fill: 'var(--aa-ink-muted,#6b6b6b)',
                    fontFamily: 'var(--aa-font-mono, monospace)',
                    fontSize: 11,
                }}
                tickLine={false}
                width={36}
            />
            <Tooltip
                contentStyle={{
                    background: 'var(--aa-canvas,#ffffff)',
                    border: '1px solid var(--aa-border,#e8e6e1)',
                    borderRadius: '6px',
                    fontFamily: 'var(--aa-font-mono, monospace)',
                    fontSize: '11px',
                }}
                formatter={(value, name) => [
                    Array.isArray(value)
                        ? value.join(', ')
                        : (value ?? 'No value'),
                    String(name),
                ]}
                labelFormatter={(label) =>
                    `${payload.axes.x.label}: ${formatTooltipAxisLabel(
                        label,
                        payload.axes.x.type,
                        timezone,
                    )}`
                }
            />
        </Fragment>
    );
}

function buildChartRows(
    payload: ParsedChartPayload,
    series: ReadonlyArray<SeriesDescriptor>,
    timeZone: string,
): ReadonlyArray<ChartRow> {
    const rows = new Map<string, ChartRow>();
    payload.series.forEach((seriesItem, seriesIndex) => {
        const dataKey = series[seriesIndex]?.dataKey;
        if (!dataKey) {
            return;
        }
        seriesItem.points.forEach((point) => {
            const orderKey = pointOrderKey(point.x, payload.axes.x.type);
            const existingRow = rows.get(orderKey);
            const nextRow = existingRow ?? {
                orderKey,
                x: point.x,
                xLabel: formatAxisValue(point.x, payload.axes.x.type, timeZone),
                values: {},
            };
            nextRow.values[dataKey] = point.y;
            rows.set(orderKey, nextRow);
        });
    });
    return sortRows([...rows.values()], payload.axes.x.type);
}

function sortRows(
    rows: Array<ChartRow>,
    axisType: ParsedChartPayload['axes']['x']['type'],
): ReadonlyArray<ChartRow> {
    if (axisType === 'temporal') {
        return rows.sort((left, right) => {
            const leftTimestamp =
                typeof left.x === 'string'
                    ? parseTemporalInstant(left.x)
                    : null;
            const rightTimestamp =
                typeof right.x === 'string'
                    ? parseTemporalInstant(right.x)
                    : null;
            return (leftTimestamp ?? 0) - (rightTimestamp ?? 0);
        });
    }
    if (axisType === 'quantitative') {
        return rows.sort((left, right) => Number(left.x) - Number(right.x));
    }
    return rows;
}

function pointOrderKey(
    value: number | string,
    axisType: ParsedChartPayload['axes']['x']['type'],
): string {
    if (axisType === 'temporal' && typeof value === 'string') {
        const timestamp = parseTemporalInstant(value);
        if (timestamp !== null) {
            return `temporal:${timestamp}`;
        }
    }
    return `${typeof value}:${String(value)}`;
}

function formatAxisValue(
    value: number | string,
    axisType: ParsedChartPayload['axes']['x']['type'],
    timeZone: string,
): string {
    if (axisType === 'temporal' && typeof value === 'string') {
        return formatTemporalAxisValue(value, timeZone);
    }
    if (typeof value === 'number') {
        return value.toLocaleString(undefined, {
            maximumFractionDigits: 2,
        });
    }
    return value;
}

function parseTemporalInstant(value: string): number | null {
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return null;
    }
    // Naive ISO strings and bare YYYY-MM-DD dates are treated as UTC to keep day buckets stable.
    const normalized = ISO_DATE_RE.test(trimmed)
        ? `${trimmed}T00:00:00Z`
        : /(?:[zZ]|[+-]\d{2}:\d{2})$/.test(trimmed)
          ? trimmed
          : `${trimmed}Z`;
    const timestamp = Date.parse(normalized);
    return Number.isNaN(timestamp) ? null : timestamp;
}

function formatTooltipAxisLabel(
    value: unknown,
    axisType: ParsedChartPayload['axes']['x']['type'],
    timeZone: string,
): string {
    if (axisType === 'temporal' && typeof value === 'string') {
        const trimmed = value.trim();
        return isTemporalChartInput(trimmed)
            ? formatTemporalAxisValue(trimmed, timeZone)
            : trimmed;
    }
    if (axisType === 'quantitative') {
        if (typeof value === 'number') {
            return formatAxisValue(value, axisType, timeZone);
        }
        if (typeof value === 'string' && value.trim() !== '') {
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) {
                return formatAxisValue(parsed, axisType, timeZone);
            }
        }
    }
    return String(value ?? 'Unknown');
}

function formatTemporalAxisValue(value: string, timeZone: string): string {
    const trimmed = value.trim();
    if (ISO_DATE_RE.test(trimmed)) {
        return new Intl.DateTimeFormat(undefined, {
            day: 'numeric',
            month: 'short',
            timeZone: 'UTC',
            year: 'numeric',
        }).format(new Date(`${trimmed}T00:00:00Z`));
    }
    const timestamp = parseTemporalInstant(trimmed);
    if (timestamp === null) {
        return trimmed;
    }
    return new Intl.DateTimeFormat(undefined, {
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        month: 'short',
        timeZone,
        year: 'numeric',
    }).format(new Date(timestamp));
}

function isTemporalChartInput(value: string): boolean {
    return ISO_DATE_RE.test(value) || value.includes('T');
}

function resolveChartTimezone({
    artifactTimeWindow,
    organizationTimezone,
}: {
    readonly artifactTimeWindow: unknown;
    readonly organizationTimezone: string;
}): string {
    if (
        artifactTimeWindow &&
        typeof artifactTimeWindow === 'object' &&
        !Array.isArray(artifactTimeWindow)
    ) {
        const timezone = (artifactTimeWindow as Record<string, unknown>)[
            'timezone'
        ];
        if (typeof timezone === 'string' && timezone.trim()) {
            return timezone;
        }
    }
    return organizationTimezone;
}

function formatChartKind(kind: ParsedChartPayload['chart_kind']): string {
    return kind.charAt(0).toUpperCase() + kind.slice(1);
}

function assertNever(value: never): never {
    throw new Error(`Unsupported chart kind: ${String(value)}`);
}
